/**
 * @fileoverview Presentational sections for event attendee registration.
 * Split out of EventRegistrationModal so each section stays a small function.
 */
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  getSelectedOption,
  getProfileFieldValue,
  getSelectedCountryCodeOption,
} from "../../utils/common.js";
import {
  getOperationEnvironmentOptions,
  getGenderOptions,
  getPositionOptions,
  getAreaOptions,
  getCountryCodeOptions,
} from "../../data/selectOptions";
import CountryCodeSelector from "../../components/CountryCodeSelector";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import styles from "./index.module.css";

const ERROR_BORDER_COLOR = "#FF0000";
const DEFAULT_BORDER_COLOR = "#909092";
const VALIDATION_ERROR = "error";
const DEFAULT_COUNTRY_CODE_WIDTH = "115%";
const COUNTRY_CODE_WIDTH_BY_DEVICE = {
  laptop: "130%",
  tablet: "90%",
  smallMobile: "130%",
  mediumMobile: "140%",
};

const SELECT_STYLES_FULL = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "8px",
    borderColor: DEFAULT_BORDER_COLOR,
    width: "100%",
    height: "30px",
    fontSize: "16px",
    cursor: "pointer",
    textOverflow: "ellipsis",
    textWrap: "nowrap",
    textAlign: "left",
    maxLines: 1,
  }),
  container: (baseStyles) => ({
    ...baseStyles,
    textAlign: "left",
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
  }),
  input: (baseStyles) => ({
    ...baseStyles,
  }),
  option: (baseStyles) => ({
    ...baseStyles,
    textAlign: "left",
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const SELECT_STYLES_COMPACT = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "8px",
    width: "100%",
    height: "30px",
    fontSize: "16px",
    cursor: "pointer",
    borderColor: DEFAULT_BORDER_COLOR,
  }),
  container: (baseStyles) => ({
    ...baseStyles,
    textAlign: "left",
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
  }),
  input: (baseStyles) => ({
    ...baseStyles,
  }),
  option: (baseStyles) => ({
    ...baseStyles,
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const SELECT_STYLES_COMPACT_NARROW = {
  ...SELECT_STYLES_COMPACT,
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    maxWidth: "120px",
  }),
};

const formatNombreOption = (option) => <span>{option.nombre}</span>;
const nombreOptionValue = (option) => option.nombre;

const isValidationError = (validationState) =>
  validationState === VALIDATION_ERROR;

const validationInputClass = (baseClass, errorClass, validationState) =>
  isValidationError(validationState)
    ? `${baseClass} ${errorClass}`
    : baseClass;

const validationBorderColor = (validationState) =>
  isValidationError(validationState) ? ERROR_BORDER_COLOR : DEFAULT_BORDER_COLOR;

const isBlankValue = (value) =>
  value === null || value === undefined || value === "";

const additionalFieldHasError = (validationState, showError, value) =>
  isValidationError(validationState) || (showError && isBlankValue(value));

const getCountryCodeSelectorWidth = (deviceType) =>
  COUNTRY_CODE_WIDTH_BY_DEVICE[deviceType] ?? DEFAULT_COUNTRY_CODE_WIDTH;

const CompanyInfoSection = ({
  t,
  formData,
  isFormDisabled,
  profileOptions,
  sectors,
  subSectors,
  branches,
  selectSectorRef,
  selectSubsectorRef,
  selectBranchRef,
  setFormData,
  setSubsectors,
  setBranches,
  getErrorMessage,
  getFieldValidationState,
  handleFieldChange,
  handleChange,
  operationEnvironmentOptions,
  handleAmbitoOperacionesChange,
}) => {
  /**
   * Stores the company legal name.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleCompanyNameChange = useCallback(
    (event) => {
      handleChange("razonSocial", event.target.value);
    },
    [handleChange],
  );

  /**
   * Stores the RFC in upper case and runs the RFC field validation.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleRfcChange = useCallback(
    (event) => {
      const value = event.target.value.toUpperCase();
      handleChange("RFC", value);
      handleFieldChange("companyRFC", value);
    },
    [handleChange, handleFieldChange],
  );

  /**
   * Stores the selected company profile index. A cleared select is ignored.
   * @param {{value: number}|null} selectedOption - Profile option.
   * @returns {void}
   */
  const handleProfileChange = useCallback(
    (selectedOption) => {
      if (selectedOption) {
        handleChange("perfilSeleccionado", selectedOption.value);
      }
    },
    [handleChange],
  );

  /**
   * Stores the sector and clears subsector and branch, because those lists depend on the sector.
   * @param {Object|null} selectedOption - Sector option, including `subSectores`.
   * @returns {void}
   */
  const handleSectorChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }
      setFormData((prev) => ({
        ...prev,
        sector: selectedOption._id,
        subSector: null,
        rama: null,
      }));
      selectSubsectorRef.current.clearValue();
      selectBranchRef.current.clearValue();
      setSubsectors(selectedOption.subSectores ?? []);
      setBranches([]);
    },
    [
      setFormData,
      setSubsectors,
      setBranches,
      selectSubsectorRef,
      selectBranchRef,
    ],
  );

  /**
   * Stores the subsector, clears the branch, and loads that subsector's branches.
   * @param {Object|null} selectedOption - Subsector option, including `ramas`.
   * @returns {void}
   */
  const handleSubsectorChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }
      setFormData((prev) => ({
        ...prev,
        subSector: selectedOption._id,
        rama: null,
      }));
      selectBranchRef.current.clearValue();
      setBranches(selectedOption.ramas ?? []);
    },
    [setFormData, setBranches, selectBranchRef],
  );

  /**
   * Stores the selected branch id. A cleared select is ignored.
   * @param {{_id: string}|null} selectedOption - Branch option.
   * @returns {void}
   */
  const handleBranchChange = useCallback(
    (selectedOption) => {
      if (selectedOption) {
        handleChange("rama", selectedOption._id);
      }
    },
    [handleChange],
  );

  /**
   * Stores the worker count when the text is empty or digits only, and validates it.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleWorkersChange = useCallback(
    (event) => {
      const value = event.target.value;
      if (value === "" || /^\d+$/.test(value)) {
        handleChange("numeroTotalTrabajadores", value);
        handleFieldChange("numeroTotalTrabajadores", value);
      }
    },
    [handleChange, handleFieldChange],
  );

  const rfcValidation = getFieldValidationState("companyRFC", formData.RFC || "");
  const workersValidation = getFieldValidationState(
    "numeroTotalTrabajadores",
    formData.numeroTotalTrabajadores || "",
  );
  const selectedProfileIndex = formData.perfilSeleccionado ?? 0;

  return (
    <>
      <label className={styles.modalSubtitle}>
        {t("components.eventRegistrationModal.companyInfo")}
      </label>
      <div className={styles.generalInfoSection}>
        <div className={styles.generalInfoRow}>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.companyName")}
            </label>
            <input
              type="text"
              placeholder={t(
                "components.eventRegistrationModal.companyNamePlaceholder",
              )}
              className={styles.input}
              disabled={isFormDisabled}
              style={{ borderColor: DEFAULT_BORDER_COLOR }}
              value={formData.razonSocial || ""}
              onChange={handleCompanyNameChange}
            />
          </div>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.rfc")}
            </label>
            <input
              type="text"
              placeholder={t("components.eventRegistrationModal.rfcPlaceholder")}
              className={validationInputClass(
                styles.input,
                styles.inputError,
                rfcValidation,
              )}
              disabled={isFormDisabled}
              style={{
                borderColor: validationBorderColor(rfcValidation),
                textTransform: "uppercase",
              }}
              value={formData.RFC || ""}
              onChange={handleRfcChange}
              maxLength={12}
            />
            {getErrorMessage("companyRFC") && (
              <p className={styles.errorMessage}>{getErrorMessage("companyRFC")}</p>
            )}
          </div>
        </div>
        <div className={styles.generalInfoRow}>
          <div className={styles.fullWidthContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.companyProfile")}
            </label>
            <Select
              options={profileOptions}
              isDisabled={isFormDisabled}
              value={getSelectedOption(profileOptions, selectedProfileIndex)}
              onChange={handleProfileChange}
              placeholder={t("common.select")}
              styles={SELECT_STYLES_FULL}
            />
          </div>
        </div>
        <div className={styles.generalInfoRow}>
          <div className={styles.fullWidthContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.sector")}
            </label>
            <Select
              ref={selectSectorRef}
              options={sectors}
              isDisabled={isFormDisabled}
              onChange={handleSectorChange}
              placeholder={t(
                "components.eventRegistrationModal.sectorPlaceholder",
              )}
              styles={SELECT_STYLES_FULL}
              getOptionValue={nombreOptionValue}
              formatOptionLabel={formatNombreOption}
            />
          </div>
        </div>
        <div className={styles.generalInfoRow}>
          <div className={styles.fullWidthContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.subsector")}
            </label>
            <Select
              ref={selectSubsectorRef}
              options={subSectors}
              isSearchable={false}
              isDisabled={isFormDisabled || !formData.sector}
              onChange={handleSubsectorChange}
              placeholder={t("pages.eventRegistrationModal.subsectorPlaceholder")}
              styles={SELECT_STYLES_FULL}
              getOptionValue={nombreOptionValue}
              formatOptionLabel={formatNombreOption}
            />
          </div>
        </div>
        <div className={styles.generalInfoRow}>
          <div className={styles.fullWidthContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.branch")}
            </label>
            <Select
              ref={selectBranchRef}
              options={branches}
              isDisabled={isFormDisabled || !formData.subSector}
              isSearchable={false}
              onChange={handleBranchChange}
              placeholder={t("pages.eventRegistrationModal.branchPlaceholder")}
              styles={SELECT_STYLES_COMPACT}
              getOptionValue={nombreOptionValue}
              formatOptionLabel={formatNombreOption}
            />
          </div>
        </div>
        <div className={styles.generalInfoRow}>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.totalWorkers")}
            </label>
            <input
              type="number"
              placeholder={t(
                "components.eventRegistrationModal.totalWorkersPlaceholder",
              )}
              className={`${validationInputClass(
                `${styles.input} ${styles.numberInput}`,
                styles.inputError,
                workersValidation,
              )}`}
              disabled={isFormDisabled}
              style={{ borderColor: validationBorderColor(workersValidation) }}
              value={formData.numeroTotalTrabajadores || ""}
              onChange={handleWorkersChange}
              min="0"
              step="1"
            />
            {getErrorMessage("numeroTotalTrabajadores") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("numeroTotalTrabajadores")}
              </p>
            )}
          </div>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.scope")}
            </label>
            <Select
              value={getSelectedOption(
                operationEnvironmentOptions,
                getProfileFieldValue(
                  formData,
                  "ambitoOperaciones",
                  selectedProfileIndex,
                  formData.ambitoOperaciones,
                ),
              )}
              isSearchable={false}
              options={operationEnvironmentOptions}
              onChange={handleAmbitoOperacionesChange}
              isDisabled={isFormDisabled}
              placeholder={t(
                "components.eventRegistrationModal.scopePlaceholder",
              )}
              styles={SELECT_STYLES_COMPACT_NARROW}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const PersonalInfoSection = ({
  t,
  formData,
  isFormDisabled,
  positionOptions,
  areaOptions,
  genderOptions,
  countryCodeOptions,
  deviceType,
  getErrorMessage,
  getFieldValidationState,
  handleFieldChange,
  handleChange,
}) => {
  /**
   * Stores the attendee name and runs name validation.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleNameChange = useCallback(
    (event) => {
      const value = event.target.value;
      handleChange("nombre", value);
      handleFieldChange("nombre", value);
    },
    [handleChange, handleFieldChange],
  );

  /**
   * Stores the phone country code. The field name stays `codiogPais` to match the API.
   * @param {{value: string}} value - Selected country-code option.
   * @returns {void}
   */
  const handleCountryCodeChange = useCallback(
    (value) => {
      handleChange("codiogPais", value.value);
    },
    [handleChange],
  );

  /**
   * Stores the phone number and runs phone validation.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handlePhoneChange = useCallback(
    (event) => {
      const value = event.target.value;
      handleChange("telefono", value);
      handleFieldChange("telefono", value);
    },
    [handleChange, handleFieldChange],
  );

  /**
   * Stores the email and runs email validation.
   * @param {Event} event - Input change event.
   * @returns {void}
   */
  const handleEmailChange = useCallback(
    (event) => {
      const value = event.target.value;
      handleChange("correoElectronico", value);
      handleFieldChange("correoElectronico", value);
    },
    [handleChange, handleFieldChange],
  );

  /**
   * Stores the selected job title. A cleared select is ignored.
   * @param {{value: string}|null} selectedOption - Position option.
   * @returns {void}
   */
  const handlePositionChange = useCallback(
    (selectedOption) => {
      if (selectedOption) {
        handleChange("puesto", selectedOption.value);
      }
    },
    [handleChange],
  );

  /**
   * Stores the selected area. A cleared select is ignored.
   * @param {{value: string}|null} selectedOption - Area option.
   * @returns {void}
   */
  const handleAreaChange = useCallback(
    (selectedOption) => {
      if (selectedOption) {
        handleChange("area", selectedOption.value);
      }
    },
    [handleChange],
  );

  /**
   * Stores the selected gender. A cleared select is ignored.
   * @param {{value: string}|null} selectedOption - Gender option.
   * @returns {void}
   */
  const handleGenderChange = useCallback(
    (selectedOption) => {
      if (selectedOption) {
        handleChange("genero", selectedOption.value);
      }
    },
    [handleChange],
  );

  const nameValidation = getFieldValidationState("nombre", formData.nombre || "");
  const phoneValidation = getFieldValidationState(
    "telefono",
    formData.telefono || "",
  );
  const emailValidation = getFieldValidationState(
    "correoElectronico",
    formData.correoElectronico || "",
  );

  return (
    <>
      <label className={styles.modalSubtitle}>
        {t("components.eventRegistrationModal.personalInformation")}
      </label>
      <div className={styles.generalInfoSection}>
        <div className={styles.generalInfoRow}>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.name")}
            </label>
            <input
              type="text"
              placeholder={t("components.eventRegistrationModal.namePlaceholder")}
              className={validationInputClass(
                styles.input,
                styles.inputError,
                nameValidation,
              )}
              disabled={isFormDisabled}
              style={{ borderColor: validationBorderColor(nameValidation) }}
              value={formData.nombre || ""}
              onChange={handleNameChange}
              maxLength={100}
            />
            {getErrorMessage("nombre") && (
              <p className={styles.errorMessage}>{getErrorMessage("nombre")}</p>
            )}
          </div>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.phone")}
            </label>
            <div className={styles.phoneInputContainer}>
              <CountryCodeSelector
                value={getSelectedCountryCodeOption(
                  countryCodeOptions,
                  formData.codiogPais,
                )}
                onChange={handleCountryCodeChange}
                isDisabled={isFormDisabled}
                containerWidth={getCountryCodeSelectorWidth(deviceType)}
                placeholder={t("common.select")}
              />
              <input
                type="tel"
                inputMode="numeric"
                className={validationInputClass(
                  styles.phoneInput,
                  styles.inputError,
                  phoneValidation,
                )}
                disabled={isFormDisabled}
                placeholder={t(
                  "components.contactInformation.officePhonePlaceholder",
                )}
                value={formData.telefono || ""}
                onChange={handlePhoneChange}
                style={{ borderColor: validationBorderColor(phoneValidation) }}
              />
            </div>
            {getErrorMessage("telefono") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("telefono")}
              </p>
            )}
          </div>
        </div>
        <div className={styles.generalInfoRow}>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.email")}
            </label>
            <input
              type="text"
              placeholder={t(
                "components.eventRegistrationModal.emailPlaceholder",
              )}
              className={validationInputClass(
                styles.input,
                styles.inputError,
                emailValidation,
              )}
              disabled={isFormDisabled}
              style={{ borderColor: validationBorderColor(emailValidation) }}
              value={formData.correoElectronico || ""}
              onChange={handleEmailChange}
              maxLength={100}
            />
            {getErrorMessage("correoElectronico") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("correoElectronico")}
              </p>
            )}
          </div>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.position")}
            </label>
            <Select
              value={getSelectedOption(positionOptions, formData.puesto)}
              isSearchable={false}
              options={positionOptions}
              onChange={handlePositionChange}
              isDisabled={isFormDisabled}
              placeholder={t(
                "components.eventRegistrationModal.positionPlaceholder",
              )}
              styles={SELECT_STYLES_COMPACT_NARROW}
            />
          </div>
        </div>
        <div className={styles.generalInfoRow}>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.area")}
            </label>
            <Select
              value={getSelectedOption(areaOptions, formData.area)}
              options={areaOptions}
              placeholder={t("components.eventRegistrationModal.areaPlaceholder")}
              styles={SELECT_STYLES_COMPACT_NARROW}
              className="select__control"
              onChange={handleAreaChange}
            />
          </div>
          <div className={styles.categoryContainer}>
            <label className={styles.label}>
              {t("components.eventRegistrationModal.gender")}
            </label>
            <Select
              value={getSelectedOption(genderOptions, formData.genero)}
              isSearchable={false}
              options={genderOptions}
              onChange={handleGenderChange}
              isDisabled={isFormDisabled}
              placeholder={t(
                "components.eventRegistrationModal.genderPlaceholder",
              )}
              styles={SELECT_STYLES_COMPACT_NARROW}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const AdditionalRequirementsSection = ({
  t,
  event,
  formData,
  showError,
  getErrorMessage,
  getFieldValidationState,
  handleFieldChange,
  handleChange,
}) => {
  /**
   * Stores commercial interests and validates them when the event requires that field.
   * @param {Event} eventChange - Textarea change event.
   * @returns {void}
   */
  const handleInterestsChange = useCallback(
    (eventChange) => {
      const value = eventChange.target.value;
      handleChange("interesesComerciales", value);
      handleFieldChange("interesesComerciales", value);
    },
    [handleChange, handleFieldChange],
  );

  /**
   * Stores expectations and validates them when the event requires that field.
   * @param {Event} eventChange - Textarea change event.
   * @returns {void}
   */
  const handleExpectationsChange = useCallback(
    (eventChange) => {
      const value = eventChange.target.value;
      handleChange("expectativas", value);
      handleFieldChange("expectativas", value);
    },
    [handleChange, handleFieldChange],
  );

  if (!event.interesesComercialesRequerido && !event.expectativasRequerido) {
    return null;
  }

  const interestsValidation = getFieldValidationState(
    "interesesComerciales",
    formData.interesesComerciales || "",
  );
  const expectationsValidation = getFieldValidationState(
    "expectativas",
    formData.expectativas || "",
  );
  const interestsClass = additionalFieldHasError(
    interestsValidation,
    showError,
    formData.interesesComerciales,
  )
    ? `${styles.input} ${styles.inputError}`
    : styles.input;
  const expectationsClass = additionalFieldHasError(
    expectationsValidation,
    showError,
    formData.expectativas,
  )
    ? `${styles.textarea} ${styles.inputError}`
    : styles.textarea;

  return (
    <>
      <label className={styles.modalSubtitle}>
        {t("components.eventRegistrationModal.additional")}
      </label>
      <div className={styles.optionalRequirementsSection}>
        {event.interesesComercialesRequerido && (
          <div className={styles.generalInfoRow}>
            <div className={styles.fullWidthContainer}>
              <label className={styles.label}>
                {t("components.eventRegistrationModal.interests")}
              </label>
              <input
                type="text"
                placeholder={t(
                  "components.eventRegistrationModal.interestsPlaceholder",
                )}
                className={interestsClass}
                value={formData.interesesComerciales || ""}
                onChange={handleInterestsChange}
                maxLength={255}
              />
              {getErrorMessage("interesesComerciales") && (
                <p className={styles.errorMessage}>
                  {getErrorMessage("interesesComerciales")}
                </p>
              )}
              <span className={styles.requiredMessage}>
                {t("components.eventRegistrationModal.requiredMessage")}
              </span>
            </div>
          </div>
        )}
        {event.expectativasRequerido && (
          <div className={styles.generalInfoRow}>
            <div className={styles.fullWidthContainer}>
              <label className={styles.label}>
                {t("components.eventRegistrationModal.question")}
              </label>
              <textarea
                placeholder={t(
                  "components.eventRegistrationModal.questionPlaceholder",
                )}
                className={expectationsClass}
                value={formData.expectativas || ""}
                onChange={handleExpectationsChange}
                maxLength={300}
              />
              {getErrorMessage("expectativas") && (
                <p className={styles.errorMessage}>
                  {getErrorMessage("expectativas")}
                </p>
              )}
              <span className={styles.requiredMessage}>
                {t("components.eventRegistrationModal.requiredMessage")}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const EventRegistrationForm = (props) => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceDetection();
  const profileOptions = [
    { value: 0, label: t("pages.companyProfile.profile1") },
    { value: 1, label: t("pages.companyProfile.profile2") },
    { value: 2, label: t("pages.companyProfile.profile3") },
  ];
  const operationEnvironmentOptions = getOperationEnvironmentOptions(t);
  const genderOptions = getGenderOptions(t);
  const positionOptions = getPositionOptions(t);
  const areaOptions = getAreaOptions(t);
  const countryCodeOptions = getCountryCodeOptions(t);

  return (
    <div className={styles.scrollableContent}>
      <CompanyInfoSection
        t={t}
        profileOptions={profileOptions}
        operationEnvironmentOptions={operationEnvironmentOptions}
        {...props}
      />
      <PersonalInfoSection
        t={t}
        positionOptions={positionOptions}
        areaOptions={areaOptions}
        genderOptions={genderOptions}
        countryCodeOptions={countryCodeOptions}
        deviceType={deviceType}
        {...props}
      />
      <AdditionalRequirementsSection t={t} {...props} />
    </div>
  );
};

export default EventRegistrationForm;
